import React, { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { getErrorMessage } from '../services/_apiBase';
import { fetchDeviationForm, fetchDeviationHistory, submitDeviationForm } from '../services/publicPagesApi';
import SensorCharts, { SensorMeta, HistoryRow } from './shared/SensorCharts';
import { publicBodyStyle, publicCardStyle } from './shared/PublicPage';

interface DeviationData {
    token: string;
    rule_name: string;
    device_name: string;
    alert_triggered_at: string | null;
    recovered_at: string | null;
    submitted: boolean;
    reporter_name: string | null;
    reason: string | null;
    action_taken: string | null;
    submitted_at: string | null;
    sensors: SensorMeta[];
}

const fmt = (iso: string | null) => {
    if (!iso) return '—';
    try {
        return new Date(iso).toLocaleString('sv-SE', { timeZoneName: 'short' });
    } catch { return iso; }
};

const bodyStyle = publicBodyStyle;
const cardStyle = publicCardStyle(600);
const labelStyle: React.CSSProperties = {
    display: 'block', fontWeight: 600, marginBottom: 4, fontSize: 14, color: '#444',
};
const inputStyle: React.CSSProperties = {
    width: '100%', boxSizing: 'border-box', border: '1px solid #ccc', borderRadius: 6,
    padding: '8px 10px', fontSize: 15, fontFamily: 'inherit', marginBottom: 16,
};
const btnStyle: React.CSSProperties = {
    background: '#2563eb', color: 'white', border: 'none', borderRadius: 6,
    padding: '10px 24px', fontSize: 15, cursor: 'pointer', fontFamily: 'inherit',
};

const DeviationPage: React.FC = () => {
    const { token } = useParams<{ token: string }>();
    const [searchParams] = useSearchParams();
    const contactId = searchParams.get('contact') ? Number(searchParams.get('contact')) : null;

    const [data, setData]           = useState<DeviationData | null>(null);
    const [history, setHistory]     = useState<HistoryRow[]>([]);
    const [loading, setLoading]     = useState(true);
    const [notFound, setNotFound]   = useState(false);
    const [submitted, setSubmitted] = useState(false);

    const [reporterName, setReporterName] = useState('');
    const [reason, setReason]             = useState('');
    const [actionTaken, setActionTaken]   = useState('');
    const [saving, setSaving]             = useState(false);
    const [saveError, setSaveError]       = useState<string | null>(null);

    useEffect(() => {
        if (!token) return;
        fetchDeviationForm<DeviationData>(token)
            .then(d => {
                setData(d);
                if (d.submitted) {
                    setReporterName(d.reporter_name ?? '');
                    setReason(d.reason ?? '');
                    setActionTaken(d.action_taken ?? '');
                    setSubmitted(true);
                }
            })
            .catch(err => { if (err?.response?.status === 404) setNotFound(true); })
            .finally(() => setLoading(false));
    }, [token]);

    // Once the form is loaded, pull the last hour of readings for any temperature
    // sensors in the check so we can render a graph per sensor.
    const hasSensors = (data?.sensors?.length ?? 0) > 0;
    useEffect(() => {
        if (!token || !hasSensors) return;
        fetchDeviationHistory<HistoryRow>(token)
            .then(setHistory)
            .catch(() => { /* graph is best-effort; ignore failures */ });
    }, [token, hasSensors]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!reason.trim() || !actionTaken.trim()) return;
        setSaving(true);
        setSaveError(null);
        try {
            const saved = await submitDeviationForm<DeviationData>(token!, {
                contact_id: contactId,
                reporter_name: reporterName.trim() || null,
                reason: reason.trim(),
                action_taken: actionTaken.trim(),
            });
            setData(saved);
            setSubmitted(true);
        } catch (err: any) {
            setSaveError(getErrorMessage(err, 'Kunde inte spara. Försök igen.'));
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return <div style={bodyStyle}><div style={cardStyle}><p style={{ color: '#888' }}>Laddar…</p></div></div>;
    }
    if (notFound || !data) {
        return (
            <div style={bodyStyle}>
                <div style={cardStyle}>
                    <h1 style={{ color: '#c0392b' }}>Länken hittades inte</h1>
                    <p style={{ color: '#666' }}>Länken för avvikelserapportering är ogiltig eller har redan använts.</p>
                </div>
            </div>
        );
    }

    return (
        <div style={bodyStyle}>
            <div style={cardStyle}>
                <h1 style={{ margin: '0 0 4px', fontSize: 22, color: '#1e3a5f' }}>
                    📋 Avvikelserapport
                </h1>
                <p style={{ margin: '0 0 24px', color: '#555' }}>
                    Larmregel: <strong>{data.rule_name}</strong><br />
                    Enhet: <strong>{data.device_name}</strong>
                </p>
                <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 24, fontSize: 14 }}>
                    <tbody>
                        <tr>
                            <td style={{ padding: '4px 0', color: '#666', width: '45%' }}>Larm utlöst:</td>
                            <td style={{ padding: '4px 0', fontWeight: 500 }}>{fmt(data.alert_triggered_at)}</td>
                        </tr>
                        <tr>
                            <td style={{ padding: '4px 0', color: '#666' }}>Återhämtat:</td>
                            <td style={{ padding: '4px 0', fontWeight: 500 }}>{fmt(data.recovered_at)}</td>
                        </tr>
                    </tbody>
                </table>

                <SensorCharts sensors={data.sensors} history={history} />

                {submitted ? (
                    <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 8, padding: 20 }}>
                        <p style={{ margin: '0 0 12px', color: '#166534', fontWeight: 600, fontSize: 16 }}>
                            ✅ Avvikelserapport inskickad
                        </p>
                        {data.submitted_at && (
                            <p style={{ margin: '0 0 8px', fontSize: 13, color: '#555' }}>
                                Inlämnad: {fmt(data.submitted_at)}
                            </p>
                        )}
                        {reporterName && <p style={{ margin: '0 0 4px', fontSize: 14 }}><strong>Rapportör:</strong> {reporterName}</p>}
                        <p style={{ margin: '8px 0 4px', fontSize: 14 }}><strong>Orsak:</strong></p>
                        <p style={{ margin: '0 0 12px', fontSize: 14, whiteSpace: 'pre-wrap', color: '#333' }}>{reason}</p>
                        <p style={{ margin: '0 0 4px', fontSize: 14 }}><strong>Åtgärd:</strong></p>
                        <p style={{ margin: 0, fontSize: 14, whiteSpace: 'pre-wrap', color: '#333' }}>{actionTaken}</p>
                    </div>
                ) : (
                    <form onSubmit={handleSubmit}>
                        <label style={labelStyle}>Ditt namn <span style={{ fontWeight: 400, color: '#888' }}>(valfritt)</span></label>
                        <input
                            type="text"
                            value={reporterName}
                            onChange={e => setReporterName(e.target.value)}
                            style={inputStyle}
                            placeholder="Förnamn Efternamn"
                        />
                        <label style={labelStyle}>Orsak till avvikelsen *</label>
                        <textarea
                            value={reason}
                            onChange={e => setReason(e.target.value)}
                            required
                            rows={4}
                            style={{ ...inputStyle, resize: 'vertical' }}
                            placeholder="Beskriv vad som orsakade larmet…"
                        />
                        <label style={labelStyle}>Vidtagen åtgärd *</label>
                        <textarea
                            value={actionTaken}
                            onChange={e => setActionTaken(e.target.value)}
                            required
                            rows={4}
                            style={{ ...inputStyle, resize: 'vertical' }}
                            placeholder="Beskriv vilka åtgärder som vidtogs…"
                        />
                        {saveError && (
                            <p style={{ color: '#c0392b', fontSize: 14, margin: '-8px 0 12px' }}>{saveError}</p>
                        )}
                        <button type="submit" style={btnStyle} disabled={saving}>
                            {saving ? 'Sparar…' : 'Skicka in rapport'}
                        </button>
                    </form>
                )}
                <div style={{ textAlign: 'center', marginTop: 32, fontSize: 12, color: '#aaa' }}>
                    DevIOT avvikelserapportering
                </div>
            </div>
        </div>
    );
};

export default DeviationPage;
